import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Center, Flex } from '@chakra-ui/react';
import { AxiosError } from 'axios';
import { useTranslations } from 'contexts/LocalizationContext';
import { useScannedRelevosContext } from 'contexts/ScannedRelevosContext';
import { StatusCodes } from 'http-status-codes';

import { getApplicationUsers } from '../../../../api/applicationUser';
import QrCodeScanner from '../../../../common/components/QrCodeScanner/QrCodeScanner';
import HandScanner from '../../../../common/components/Scanner/HandScanner';
import RelevoScanner from '../../common/components/RelevoScanner';
import SelectCategories from '../SelectCategories';
import { ItemAssignmentScannersModes, ItemAssignmentSelectMode, UserSelectMode } from './types';

const UserSelect = ({ mode }: { mode: UserSelectMode }) => {
  const translations = useTranslations();
  const { selectItemMode, applicationUser, setApplicationUser, quickContact, restaurantId } =
    useScannedRelevosContext();
  const { uId } = useParams<{ uId?: string }>();
  const [code, setCode] = useState('');

  const isClientSelected = useMemo(() => applicationUser || quickContact, [applicationUser, quickContact]);

  const showError = useCallback((error: AxiosError) => {
    if (error.response) {
      toast.error(
        <b>
          {translations(error.response.status === StatusCodes.NOT_FOUND ? 'client_not_found' : 'something_went_wrong')}
        </b>,
      );
    }
    // FYI: translations in deps cause endless re-rendering
    // eslint-disable-next-line
  }, []);

  const handleScan = useCallback(
    async (uniqueId: string | null) => {
      let isUserScanned = false;
      if (uniqueId && !isUserScanned && !isClientSelected) {
        getApplicationUsers(uniqueId)
          .then((applicationUsers) => {
            isUserScanned = true;
            if (applicationUsers.length === 1) {
              setApplicationUser(applicationUsers[0]);
            }
          })
          .catch((error: AxiosError) => {
            showError(error);
          });
      }
      setCode('');
    },
    [isClientSelected, setApplicationUser, showError],
  );

  useEffect(() => {
    if (uId) {
      handleScan(uId);
    }
  }, [handleScan, uId]);

  useEffect(() => {
    if (code) {
      handleScan(code);
    }
  }, [handleScan, code]);

  return (
    <Flex w={'100%'}>
      {!isClientSelected && ItemAssignmentScannersModes.includes(selectItemMode) ? (
        <Flex py={0} wrap="wrap" p={6} h="100%" my={'auto'}>
          <Center m={'auto'} p={2}>
            {mode === UserSelectMode.UPGRADE ? <QrCodeScanner onScan={setCode} /> : null}
            {[UserSelectMode.ASSIGNMENT, UserSelectMode.RETURN].includes(mode) &&
            selectItemMode === ItemAssignmentSelectMode.CAMERA_SCAN ? (
              <QrCodeScanner onScan={setCode} />
            ) : null}
            {[UserSelectMode.ASSIGNMENT, UserSelectMode.RETURN].includes(mode) &&
            selectItemMode === ItemAssignmentSelectMode.HAND_SCANNER_SCAN ? (
              <HandScanner onScan={setCode} />
            ) : null}
          </Center>
        </Flex>
      ) : null}

      {selectItemMode === ItemAssignmentSelectMode.SELECT_CATEGORY && mode === UserSelectMode.ASSIGNMENT ? (
        <Flex w={'100%'}>
          <SelectCategories />
        </Flex>
      ) : null}

      {isClientSelected &&
      restaurantId &&
      ItemAssignmentScannersModes.includes(selectItemMode) &&
      mode === UserSelectMode.ASSIGNMENT ? (
        <Flex w={'100%'}>
          <RelevoScanner />
        </Flex>
      ) : null}
    </Flex>
  );
};

export default UserSelect;
